import React, { useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import { AlertTriangle, Database, RefreshCcw, Trash2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const COLLECTIONS_WIPED = [
    "employees", "contracts", "attendance_events", "attendance_daily",
    "payroll_periods", "payroll_lines", "payroll_funding",
    "wage_rates", "shift_rules",
    "leave_requests", "leave_balances",
    "discipline_records", "tasks", "ai_interactions",
];

const PRESERVED = ["users", "modules", "leave_types", "public_holidays", "system_settings", "permissions"];

function CountsTable({ counts, title }) {
    if (!counts) return null;
    const entries = Object.entries(counts).filter(([, v]) => v != null);
    if (!entries.length) return null;
    return (
        <div className="mt-3 text-xs">
            <div className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)] mb-1">
                {title}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 mono">
                {entries.map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                        <span className="text-[color:var(--asl-muted)]">{k}</span>
                        <span className="font-semibold">{v}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ConfirmModal({ open, onOpenChange, title, description, confirmWord, danger, busy, onConfirm }) {
    const [typed, setTyped] = useState("");
    const ok = typed.trim().toUpperCase() === confirmWord;
    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v) setTyped("");
                onOpenChange(v);
            }}
        >
            <DialogContent data-testid={`confirm-modal-${confirmWord.toLowerCase()}`}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 asl-navy-text">
                        <AlertTriangle className={`h-5 w-5 ${danger ? "text-red-600" : "asl-crimson-text"}`} />
                        {title}
                    </DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <Label htmlFor="confirm-input">
                        Type <span className="font-bold mono">{confirmWord}</span> to confirm:
                    </Label>
                    <Input
                        id="confirm-input"
                        autoFocus
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder={confirmWord}
                        data-testid={`confirm-input-${confirmWord.toLowerCase()}`}
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant={danger ? "destructive" : "default"}
                        disabled={!ok || busy}
                        onClick={onConfirm}
                        data-testid={`confirm-submit-${confirmWord.toLowerCase()}`}
                    >
                        {busy ? "Working…" : `Confirm ${confirmWord}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function DemoDataAdmin() {
    const navigate = useNavigate();
    const [wipeOpen, setWipeOpen] = useState(false);
    const [reseedOpen, setReseedOpen] = useState(false);
    const [seedOpen, setSeedOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [lastWipe, setLastWipe] = useState(null);
    const [lastSeed, setLastSeed] = useState(null);

    const runWipe = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/demo-data/wipe", { confirm: true });
            setLastWipe(data);
            setWipeOpen(false);
            toast.success("Business data wiped.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const runSeed = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/demo-data/seed-somali", { confirm: true });
            setLastSeed(data);
            setSeedOpen(false);
            toast.success("Somali demo dataset seeded.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const runReseed = async () => {
        setBusy(true);
        try {
            const { data: wipeRes } = await api.post("/admin/demo-data/wipe", { confirm: true });
            setLastWipe(wipeRes);
            const { data: seedRes } = await api.post("/admin/demo-data/seed-somali", { confirm: true });
            setLastSeed(seedRes);
            setReseedOpen(false);
            toast.success("Reseed complete — wiped + reseeded Somali dataset.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <AppShell title="Demo Data">
            <section className="space-y-5" data-testid="demo-data-page">
                <div className="flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/admin/settings")}
                        data-testid="demo-data-back"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" /> Settings
                    </Button>
                </div>

                <div className="asl-card p-4">
                    <div className="flex items-start gap-3">
                        <div className="p-2 asl-navy-bg text-white rounded-md">
                            <Database className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-bold asl-navy-text">Demo Data Management</h2>
                            <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                                Use these tools to reset the platform to a clean, predictable demo state.
                                User accounts, modules, and configuration are <span className="font-semibold">never</span> wiped.
                            </p>
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)] mb-1">
                                        Wiped (business data)
                                    </div>
                                    <div className="mono leading-relaxed">{COLLECTIONS_WIPED.join(", ")}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)] mb-1">
                                        Preserved
                                    </div>
                                    <div className="mono leading-relaxed">{PRESERVED.join(", ")}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="demo-data-actions">
                    {/* Reseed (wipe + seed) */}
                    <div className="asl-card p-5 flex flex-col" data-testid="card-reseed">
                        <div className="flex items-center gap-2">
                            <RefreshCcw className="h-5 w-5 asl-crimson-text" />
                            <h3 className="text-base font-bold asl-navy-text">Reset & Reseed</h3>
                        </div>
                        <p className="text-sm text-[color:var(--asl-muted)] mt-2 flex-1">
                            One-click action. Wipes all business data and reseeds with the Somali demo dataset.
                            Best for getting a clean predictable state for a demo run.
                        </p>
                        <Button
                            variant="default"
                            className="mt-4 self-start"
                            onClick={() => setReseedOpen(true)}
                            data-testid="btn-open-reseed"
                        >
                            <RefreshCcw className="h-4 w-4 mr-2" /> Reset & Reseed
                        </Button>
                    </div>

                    {/* Seed only */}
                    <div className="asl-card p-5 flex flex-col" data-testid="card-seed-only">
                        <div className="flex items-center gap-2">
                            <Database className="h-5 w-5 asl-navy-text" />
                            <h3 className="text-base font-bold asl-navy-text">Seed Somali Dataset</h3>
                        </div>
                        <p className="text-sm text-[color:var(--asl-muted)] mt-2 flex-1">
                            Adds Somali-centric demo data on top of whatever already exists. Use this when you
                            want to layer in extra demo rows without wiping current data first.
                        </p>
                        <Button
                            variant="outline"
                            className="mt-4 self-start"
                            onClick={() => setSeedOpen(true)}
                            data-testid="btn-open-seed"
                        >
                            <Database className="h-4 w-4 mr-2" /> Seed only
                        </Button>
                    </div>

                    {/* Wipe only */}
                    <div className="asl-card p-5 flex flex-col" data-testid="card-wipe-only">
                        <div className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-red-600" />
                            <h3 className="text-base font-bold asl-navy-text">Wipe All Business Data</h3>
                        </div>
                        <p className="text-sm text-[color:var(--asl-muted)] mt-2 flex-1">
                            Destructive. Removes all employees, contracts, attendance, payroll, leave,
                            discipline, and task records. Users, modules, and configuration are preserved.
                        </p>
                        <Button
                            variant="destructive"
                            className="mt-4 self-start"
                            onClick={() => setWipeOpen(true)}
                            data-testid="btn-open-wipe"
                        >
                            <Trash2 className="h-4 w-4 mr-2" /> Wipe All
                        </Button>
                    </div>
                </div>

                {/* Last results */}
                {(lastWipe || lastSeed) && (
                    <div className="asl-card p-4" data-testid="demo-data-last-result">
                        <h3 className="text-sm font-bold asl-navy-text">Last operation</h3>
                        {lastWipe && (
                            <>
                                <div className="text-xs text-[color:var(--asl-muted)] mt-2">
                                    Wiped at <span className="mono">{lastWipe.wiped_at}</span>
                                </div>
                                <CountsTable counts={lastWipe.summary} title="Documents deleted" />
                            </>
                        )}
                        {lastSeed && (
                            <>
                                <div className="text-xs text-[color:var(--asl-muted)] mt-3">
                                    Seeded at <span className="mono">{lastSeed.seeded_at}</span> · scope:{" "}
                                    <span className="mono">{(lastSeed.scope || []).join(", ")}</span>
                                </div>
                                <CountsTable counts={lastSeed.counts} title="Documents created" />
                            </>
                        )}
                    </div>
                )}
            </section>

            <ConfirmModal
                open={wipeOpen}
                onOpenChange={setWipeOpen}
                title="Wipe all business data?"
                description="This will delete employees, contracts, attendance, payroll, leave, discipline, and tasks. Users and modules will be preserved. This cannot be undone."
                confirmWord="WIPE"
                danger
                busy={busy}
                onConfirm={runWipe}
            />
            <ConfirmModal
                open={reseedOpen}
                onOpenChange={setReseedOpen}
                title="Reset & reseed demo dataset?"
                description="This will wipe all current business data and then seed a fresh Somali-centric demo dataset. Users and modules are preserved. This cannot be undone."
                confirmWord="RESEED"
                danger
                busy={busy}
                onConfirm={runReseed}
            />
            <ConfirmModal
                open={seedOpen}
                onOpenChange={setSeedOpen}
                title="Seed Somali demo dataset?"
                description="This will add Somali-centric demo data on top of whatever already exists. Existing data is not touched."
                confirmWord="SEED"
                busy={busy}
                onConfirm={runSeed}
            />
        </AppShell>
    );
}
